import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';

/**
 * A class for the Tabs module
 */
export default class Tabs {
  /**
   * Constructor for the Tabs class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element = document) {
    this.element = element;
    this.init();
  }

  /**
   * Initializes the view by caching DOM references
   * and enabling tab functionality.
   * @returns {Tabs} A reference to the current instance
   * @chainable
   */
  init() {
    this.cacheDomReferences().enable();
    this.openTabFromUrl();

    return this;
  }

  /**
   * Cache DOM References.
   *
   * Finds and stores all necessary DOM elements used in the view.
   * @returns {Tabs} A reference to the current instance
   * @chainable
   */
  cacheDomReferences() {
    this.tabs = this.element.querySelectorAll(`.${CLASS_NAMES.TAB}`);
    this.contents = this.element.querySelectorAll(`.${CLASS_NAMES.CONTENT}`);
    this.tierSelectionContents = this.element.querySelectorAll(
      `.${CLASS_NAMES.TIER_SELECTION_CONTENT}`
    );

    return this;
  }

  static slugify(text) {
    return text
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  openTabFromUrl() {
    try {
      const url = new URL(window.location.href);
      const param = url.searchParams.get(EVENTS.TAB);
      if (!param) return this;

      const matchingTab = Array.from(this.tabs).find((tab) => {
        const raw =
          tab.getAttribute(SELECTORS.DATA_TAB_TEXT) || tab.textContent || '';
        return this.constructor.slugify(raw) === param;
      });

      if (matchingTab) {
        matchingTab.click();
        return this;
      }
    } catch (e) {
      // ignore URL parsing errors
    }

    return this;
  }

  /**
   * Enable the tab behavior by binding the necessary event listeners.
   * @returns {Tabs} A reference to the current instance
   * @chainable
   */
  enable() {
    if (!this.tabs.length) return this;

    this.tabs.forEach((tab) => {
      tab.addEventListener(EVENTS.CLICK, () => {
        const targetId = tab.getAttribute(SELECTORS.DATA_ALVO);

        if (!targetId) return;

        // 1 Close all tabs and contents
        this.tabs.forEach((item) => {
          item.classList.remove(CLASS_NAMES.OPEN);
        });

        this.contents.forEach((content) => {
          content.classList.remove(CLASS_NAMES.OPEN);
        });

        this.tierSelectionContents.forEach((tierSelectionContent) => {
          tierSelectionContent.classList.remove(CLASS_NAMES.OPEN);
        });

        // 2 Activate the clicked tab and target content
        const targetContent = document.getElementById(targetId);

        if (!targetContent) return;

        tab.classList.add(CLASS_NAMES.OPEN);
        targetContent.classList.add(CLASS_NAMES.OPEN);

        const targetTierSelectionContent = document.getElementById(
          `${CLASS_NAMES.TIER_SELECTION_CONTENT}-${targetId.split('-').pop()}`
        );

        if (targetTierSelectionContent) {
          targetTierSelectionContent.classList.add(CLASS_NAMES.OPEN);
        }

        // Update the URL with a slugified version of the tab text
        const rawTabText =
          tab.getAttribute(SELECTORS.DATA_TAB_TEXT) || tab.textContent || '';
        const slug = this.constructor.slugify(rawTabText);

        try {
          const url = new URL(window.location.href);

          url.searchParams.set(EVENTS.TAB, slug);
          window.history.pushState(null, '', url.toString());
        } catch (e) {
          // ignore URL update errors in older environments
        }
      });
    });

    return this;
  }
}
